import { useState, useEffect } from "react";

/**
 * Hook to track document visibility state
 * Abstraction over the Page Visibility API
 *
 * @returns {boolean} isVisible - true if the document is currently visible
 */
export function usePageVisibility(): boolean {
  const [isVisible, setIsVisible] = useState<boolean>(
    typeof document !== "undefined"
      ? document.visibilityState === "visible"
      : true,
  );

  useEffect(() => {
    if (typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
    };

    const handleFocus = () => {
      setIsVisible(true);
    };

    // Handle both visibilitychange (tab switch) and focus (window focus)
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Initial check
    setIsVisible(document.visibilityState === "visible");

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  return isVisible;
}
